import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  describeDiagnosticError,
  runDiagnosticProbe,
  type ProbeResult,
} from '../auth/AuthDiagnostics';
import { useAuth } from '../auth/AuthProvider';
import { runningEnv } from '../env/appEnv';
import { readCachedI18n } from '../i18n/cache';
import { useT } from '../i18n';
import { activateBetaEnvironment, returnToProduction, type EnvSwitchResult } from '../native/ota';
// Single Source of Truth fuer die Web-Bundle-Version (wird vom OTA-Layer
// gepflegt/gemeldet) — nicht mehr hartkodiert '0.1.0'.
import { APP_VERSION } from '../native/appVersion';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';
const DIAGNOSTICS_OPEN_EVENT = 'lorath:diagnostics-open';
const TAP_TARGET = 5;
const TAP_WINDOW_MS = 3000;
const I18N_DIAGNOSE_DELAY_MS = 10000;

interface CapacitorWindow extends Window {
  Capacitor?: {
    getPlatform?: () => string;
  };
}

interface DiagnosticsOverlayProps {
  open: boolean;
  onClose: () => void;
}

function readPlatform(): string {
  if (typeof window === 'undefined') return 'unknown';
  return (window as CapacitorWindow).Capacitor?.getPlatform?.() ?? 'web';
}

function readOnlineState(): string {
  if (typeof navigator === 'undefined') return 'unknown';
  return navigator.onLine ? 'online' : 'offline';
}

function testLocalStorage(): string {
  if (typeof window === 'undefined') return 'not available';
  const key = '__lorath_diag_storage_test__';
  try {
    window.localStorage.setItem(key, 'ok');
    const value = window.localStorage.getItem(key);
    window.localStorage.removeItem(key);
    return value === 'ok' ? 'ok' : `read mismatch: ${String(value)}`;
  } catch (error) {
    return describeDiagnosticError(error);
  }
}

function recordRapidTap(tapsRef: { current: number[] }, onHit: () => void): void {
  const now = Date.now();
  tapsRef.current = [...tapsRef.current.filter((tapAt) => now - tapAt <= TAP_WINDOW_MS), now];
  if (tapsRef.current.length < TAP_TARGET) return;

  tapsRef.current = [];
  onHit();
}

function isHeaderTitleTap(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  const header = target.closest('header');
  if (!header) return false;
  const title = target.closest('[class*="title"]');
  return Boolean(title && header.contains(title));
}

function formatValue(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined || value === '') return '(empty)';
  return String(value);
}

/** Ergebnis des Umgebungswechsels → i18n-Schluessel fuer die Rueckmeldung. */
function envMessageKey(result: EnvSwitchResult): string {
  if (result === 'no_update') return 'settings.beta.activate_no_update';
  if (result === 'not_native') return 'settings.beta.activate_not_native';
  if (result === 'error') return 'settings.beta.switch_error';
  return 'settings.beta.switching';
}

function openDiagnosticsOverlay(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(DIAGNOSTICS_OPEN_EVENT));
}

export { openDiagnosticsOverlay };

export function DiagnosticsController() {
  const { diagnostics } = useT();
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [showI18nDiagnoseButton, setShowI18nDiagnoseButton] = useState(false);
  const i18nDiagnosticsRef = useRef(diagnostics);
  const headerTapsRef = useRef<number[]>([]);

  useEffect(() => {
    i18nDiagnosticsRef.current = diagnostics;
    if (diagnostics.mapSizes.active > 0) setShowI18nDiagnoseButton(false);
  }, [diagnostics]);

  useEffect(() => {
    const handleOpen = () => setOpen(true);
    window.addEventListener(DIAGNOSTICS_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(DIAGNOSTICS_OPEN_EVENT, handleOpen);
  }, []);

  useEffect(() => {
    setShowI18nDiagnoseButton(false);
    const timer = window.setTimeout(() => {
      const latest = i18nDiagnosticsRef.current;
      const cachedMaps = readCachedI18n(latest.lang);
      if (latest.mapSizes.active === 0 && !cachedMaps) {
        setShowI18nDiagnoseButton(true);
      }
    }, I18N_DIAGNOSE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [diagnostics.lang]);

  useEffect(() => {
    if (auth.status !== 'authed') return undefined;

    const handleClick = (event: MouseEvent) => {
      if (!isHeaderTitleTap(event.target)) return;
      recordRapidTap(headerTapsRef, () => setOpen(true));
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [auth.status]);

  return (
    <>
      {showI18nDiagnoseButton && !open ? (
        <button type="button" style={floatingButtonStyle} onClick={() => setOpen(true)}>
          Diagnose
        </button>
      ) : null}
      <DiagnosticsOverlay open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function DiagnosticsOverlay({ open, onClose }: DiagnosticsOverlayProps) {
  const { t, diagnostics } = useT();
  const auth = useAuth();
  const [probes, setProbes] = useState<ProbeResult[]>([]);
  const [running, setRunning] = useState(false);
  const [localStorageStatus, setLocalStorageStatus] = useState('not tested');
  const platform = useMemo(readPlatform, []);
  // R6-02-e: zweiter Einstieg zum Umgebungswechsel. Das Overlay ist per
  // Fuenf-Tipp auf der ANMELDESEITE erreichbar — genau der Fall, in dem der
  // Rollen-Gate der Einstellungen nicht greifen kann, weil niemand angemeldet
  // ist. Ohne diesen Weg gaebe es aus der Beta keinen Rueckweg, sobald man dort
  // vor dem Login steht.
  const [envBusy, setEnvBusy] = useState(false);
  const [envMessage, setEnvMessage] = useState<string | null>(null);
  const env = runningEnv();

  const applyEnvResult = useCallback((result: EnvSwitchResult) => {
    setEnvMessage(result === 'switching' ? null : t(envMessageKey(result)));
  }, [t]);

  const handleActivateBeta = useCallback(async () => {
    setEnvBusy(true);
    setEnvMessage(null);
    try {
      applyEnvResult(await activateBetaEnvironment());
    } catch {
      setEnvMessage(t('settings.beta.switch_error'));
    } finally {
      setEnvBusy(false);
    }
  }, [applyEnvResult, t]);

  const handleReturnToProduction = useCallback(async () => {
    setEnvBusy(true);
    setEnvMessage(null);
    try {
      applyEnvResult(await returnToProduction());
    } catch {
      setEnvMessage(t('settings.beta.switch_error'));
    } finally {
      setEnvBusy(false);
    }
  }, [applyEnvResult, t]);

  const runProbes = useCallback(async () => {
    setRunning(true);
    setLocalStorageStatus(testLocalStorage());
    setProbes([
      { label: 'GET /api/public/stats', outcome: 'running...' },
      { label: 'GET /api/i18n/en', outcome: 'running...' },
      { label: 'GET /api/auth/me', outcome: 'running...' },
    ]);

    const results = await Promise.all([
      runDiagnosticProbe('GET /api/public/stats', `${API_BASE}/api/public/stats`, {
        credentials: 'omit',
      }),
      runDiagnosticProbe('GET /api/i18n/en', `${API_BASE}/api/i18n/en`, {
        cache: 'no-cache',
        credentials: 'omit',
      }),
      runDiagnosticProbe('GET /api/auth/me', `${API_BASE}/api/auth/me`, {
        cache: 'no-cache',
        credentials: 'include',
      }, {
        includeStoredBearer: true,
      }),
    ]);

    setProbes(results);
    setRunning(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    void runProbes();
  }, [open, runProbes]);

  if (!open) return null;

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="Lorath diagnostics">
      <div style={toolbarStyle}>
        <div>
          <div style={eyebrowStyle}>Lorath Diagnostics</div>
          <div style={mutedStyle}>Screenshot-friendly runtime telemetry</div>
        </div>
        <button type="button" style={closeButtonStyle} onClick={onClose}>Close</button>
      </div>

      <Section title="App">
        <Row label="version" value={APP_VERSION} />
        <Row label="platform" value={platform} />
        <Row label="API_BASE" value={API_BASE || '(empty)'} />
        <Row label="navigator.onLine" value={readOnlineState()} />
        <Row label="localStorage" value={localStorageStatus} />
      </Section>

      <Section title="Environment">
        <Row label="running" value={env} />
        <div style={rowsStyle}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {env === 'production' ? (
              <button
                type="button"
                style={probeButtonStyle}
                disabled={envBusy}
                onClick={() => void handleActivateBeta()}
              >
                {t('settings.beta.activate_beta')}
              </button>
            ) : (
              <button
                type="button"
                style={probeButtonStyle}
                disabled={envBusy}
                onClick={() => void handleReturnToProduction()}
              >
                {t('settings.beta.return_production')}
              </button>
            )}
          </div>
          {envMessage ? <Row label="result" value={envMessage} /> : null}
        </div>
      </Section>

      <Section title="i18n">
        <Row label="lang" value={diagnostics.lang} />
        <Row label="active map size" value={diagnostics.mapSizes.active} />
        <Row label="en map size" value={diagnostics.mapSizes.en} />
        <Row label="fetchCount" value={diagnostics.fetchCount} />
        <Row label="lastSuccessAt" value={diagnostics.lastSuccessAt ?? '(none)'} />
        <Row
          label="lastError"
          value={diagnostics.lastError
            ? `${diagnostics.lastError.url}: ${diagnostics.lastError.message}`
            : '(none)'}
        />
      </Section>

      <Section title="Auth">
        <Row label="status" value={auth.status} />
        <Row label="offline" value={auth.offline ? 'true' : 'false'} />
        <Row label="lastError" value={auth.lastError ? describeDiagnosticError(auth.lastError) : '(none)'} />
      </Section>

      <Section
        title="Live Probes"
        action={(
          <button type="button" style={probeButtonStyle} onClick={() => void runProbes()} disabled={running}>
            {running ? 'Running...' : 'Run again'}
          </button>
        )}
      >
        {probes.map((probe) => (
          <Row key={probe.label} label={probe.label} value={probe.outcome} />
        ))}
      </Section>
    </div>
  );
}

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section style={sectionStyle}>
      <div style={sectionHeaderStyle}>
        <h2 style={sectionTitleStyle}>{title}</h2>
        {action}
      </div>
      <div style={rowsStyle}>{children}</div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string | number | boolean | null | undefined }) {
  return (
    <div style={rowStyle}>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{formatValue(value)}</div>
    </div>
  );
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 2147483647,
  overflowY: 'auto',
  padding: 'calc(14px + env(safe-area-inset-top, 0px)) 14px calc(18px + env(safe-area-inset-bottom, 0px))',
  background: 'var(--ff-bg-primary, #101e42)',
  color: 'var(--ff-text-primary, #f9f6f0)',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
  fontSize: '12px',
  lineHeight: 1.45,
  boxSizing: 'border-box',
};

const toolbarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '12px',
  marginBottom: '14px',
};

const eyebrowStyle: CSSProperties = {
  fontSize: '14px',
  fontWeight: 800,
};

const mutedStyle: CSSProperties = {
  color: 'var(--ff-text-muted, #a9a698)',
  marginTop: '2px',
};

const closeButtonStyle: CSSProperties = {
  minHeight: '36px',
  padding: '0 12px',
  border: '1px solid var(--ff-border, rgba(255,255,255,0.3))',
  borderRadius: '6px',
  background: 'var(--ff-bg-secondary, #1c294b)',
  color: 'var(--ff-text-primary, #f9f6f0)',
  font: 'inherit',
};

const sectionStyle: CSSProperties = {
  marginTop: '12px',
  paddingTop: '10px',
  borderTop: '1px solid var(--ff-border, rgba(255,255,255,0.3))',
};

const sectionHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  marginBottom: '8px',
};

const sectionTitleStyle: CSSProperties = {
  margin: 0,
  color: 'var(--ff-text-primary, #f9f6f0)',
  fontSize: '12px',
  lineHeight: 1.3,
  textTransform: 'uppercase',
};

const rowsStyle: CSSProperties = {
  display: 'grid',
  gap: '5px',
};

const rowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(110px, 34%) minmax(0, 1fr)',
  gap: '8px',
};

const labelStyle: CSSProperties = {
  color: 'var(--ff-text-muted, #a9a698)',
};

const valueStyle: CSSProperties = {
  color: 'var(--ff-text-primary, #f9f6f0)',
  overflowWrap: 'anywhere',
  whiteSpace: 'pre-wrap',
};

const probeButtonStyle: CSSProperties = {
  minHeight: '32px',
  padding: '0 10px',
  border: '1px solid var(--ff-border, rgba(255,255,255,0.3))',
  borderRadius: '6px',
  background: 'var(--ff-bg-tertiary, #273453)',
  color: 'var(--ff-text-primary, #f9f6f0)',
  font: 'inherit',
};

const floatingButtonStyle: CSSProperties = {
  position: 'fixed',
  right: 'calc(14px + env(safe-area-inset-right, 0px))',
  bottom: 'calc(82px + env(safe-area-inset-bottom, 0px))',
  zIndex: 2147483000,
  minHeight: '42px',
  padding: '0 14px',
  border: '1px solid var(--ff-border, rgba(255,255,255,0.3))',
  borderRadius: '8px',
  background: 'var(--ff-bg-secondary, #1c294b)',
  color: 'var(--ff-text-primary, #f9f6f0)',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
  fontSize: '12px',
  fontWeight: 800,
  boxShadow: '0 10px 28px rgba(0, 0, 0, 0.42)',
};
